import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ConfigService, Mutable, getUuidV4 } from '@evorait/pace-core';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { Store } from '@ngrx/store';
import * as _ from 'lodash';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { saveRecipients } from '../../store/actions/form.actions';
import { getFormRecipients } from '../../store/selectors/form.selector';
import { FormsState } from '../../store/state/pace-forms.state';
import { Recipient, initialRecipientState } from '../../types/recipient';

@Component({
    selector: 'pace-forms-recipients',
    templateUrl: './recipients.component.html',
    styleUrls: ['./recipients.component.scss'],
})
export class RecipientsComponent implements OnInit, OnDestroy {
    public recipients: Mutable<Recipient[]>;

    public destroyTrigger$ = new Subject<void>();

    public selectAllControl: FormControl;
    public recipientsGroup: FormGroup;
    public recipientsChildren: FormArray;

    public readonly maxRecipients: number;

    public faCheck = faCheck;
    public constructor(
        private readonly store: Store<FormsState>,
        public readonly modalInstance: BsModalRef,
        formBuilder: FormBuilder,
        configService: ConfigService,
    ) {
        this.maxRecipients = configService.getMaxRecipientEntries();
        this.selectAllControl = formBuilder.control({});
        this.recipientsChildren = formBuilder.array([]);
        this.recipientsGroup = formBuilder.group({
            children: this.recipientsChildren,
            checkbox: this.selectAllControl,
        });

        this.selectAllControl.valueChanges.subscribe((value) => {
            if (value !== null) {
                this.recipients = this.recipients.map((r) => ({ ...r, selected: r.defaultEmail || value }));
            }
        });
    }

    public ngOnInit(): void {
        this.store
            .select(getFormRecipients)
            .pipe(take(1))
            .subscribe((recipients) => {
                this.recipients = _.cloneDeep(recipients);
                this.updateSelectAll();
            });
    }

    public ngOnDestroy(): void {
        this.destroyTrigger$.next();
    }

    public addRecipient(): void {
        if (this.recipients.length < this.maxRecipients) {
            this.recipients = [...this.recipients, { ...initialRecipientState, id: getUuidV4() }];
            this.recipientsGroup.markAsDirty();
        }
    }

    public deleteRecipient(id: string): void {
        const index = this.recipients.findIndex((r) => r.id === id);
        this.recipients.splice(index, 1);
        this.recipients = [...this.recipients];
        this.recipientsGroup.markAsDirty();
    }

    public selectRecipient(id: string): void {
        const index = this.recipients.findIndex((r) => r.id === id);
        const recipient = this.recipients[index];
        this.recipients.splice(index, 1, { ...recipient, selected: !recipient.selected });
        this.updateSelectAll();
    }

    public saveRecipients(): void {
        this.store.dispatch(saveRecipients({ recipients: this.recipients }));
        this.modalInstance.hide();
    }

    public trackById(index: number, recipient: Recipient): string {
        return recipient.id;
    }

    public updateSelectAll(): void {
        this.selectAllControl.patchValue(
            this.recipients.every((r) => r.selected),
            { emitEvent: false },
        );
    }
}
